// Typed environment access + backend URL helpers. One place that knows where
// the separate-domain backend lives, so nothing else hard-codes a host.
//
// BACKEND-GUIDE §2: NEXT_PUBLIC_BACKEND_URL is the only backend value the
// browser is allowed to see. The tenant API key (BACKEND_API_KEY) is
// server-only and NEVER read here — it lives exclusively in the creation
// Route Handler (src/app/api/meetings/route.ts).

const RAW_BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL ?? 'http://localhost:4000';

/** Backend origin with any trailing slash stripped, e.g. `https://api.example`. */
export const BACKEND_URL = RAW_BACKEND_URL.replace(/\/+$/, '');

/**
 * Build an absolute backend URL from a path. Every browser-side call in a
 * feature's api.ts goes through this so the origin is defined once.
 */
export function backendUrl(path: string): string {
  return `${BACKEND_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Headers every direct-to-backend browser call sends. The ngrok skip header is
 * harmless off-tunnel and prevents the free-tier "visit site" interstitial from
 * ever standing in front of a JSON response (BACKEND-GUIDE §9).
 */
export const BACKEND_HEADERS: Record<string, string> = {
  'ngrok-skip-browser-warning': 'true',
};

// ── GrowithNSI central SSO (docs/sso/, docs/sales-integration/responses/MIHIR-RESPONSE-web-token-mechanism.md) ──
//
// Default OFF — mirrors the Sales app's own pattern: dev builds skip the
// cross-subdomain cookie loop entirely, only production (once Meet is
// actually served under a real `.app.growithnsi.com` subdomain) flips it on.
// The mechanism depends on a `refresh_token` HttpOnly cookie scoped to that
// exact domain — it will never be present anywhere else, including a
// staging/preview URL.
export const SSO_ENABLED = process.env.NEXT_PUBLIC_SSO_ENABLED === 'true';

export const AUTH_URL = (
  process.env.NEXT_PUBLIC_AUTH_URL ?? 'https://auth.app.growithnsi.com'
).replace(/\/+$/, '');

export const LOGIN_URL = (
  process.env.NEXT_PUBLIC_LOGIN_URL ?? 'https://login.app.growithnsi.com'
).replace(/\/+$/, '');

/**
 * Local-dev-only bypass (MIHIR-RESPONSE-web-token-mechanism.md §Q4) — lets
 * create / salesperson-join be exercised against a real JWT without the
 * cross-subdomain cookie loop, which only works on a real
 * `.app.growithnsi.com` host. NEVER set this in a real deployment — it
 * would bake a live credential into the public client bundle.
 */
export const DEV_TOKEN = process.env.NEXT_PUBLIC_DEV_TOKEN || null;
